import { useMemo, useRef } from 'react';
import { EditTraversalCommands } from './editTraversalCommands';
import { getToggleGraphNodeSelection } from 'viewpointBuilder/toggleGraphNodeSelection';
import { ViewpointBuilderFooter } from 'viewpointBuilder/components/ViewpointBuilderFooter';
import { SimpleGraphView } from 'viewpointBuilder/components/SimpleGraph/SimpleGraphView';
import { createGraphInterface } from 'viewpointBuilder/components/SimpleGraph/GraphInterface';
import { EditTraversalOptionsPanel } from 'viewpointBuilder/components/EditTraversalOptionsPanel';
import { SimpleGraphContainerWithGreyBackground } from 'viewpointBuilder/components/SimpleGraph/SimpleGraphViewContainer';
import { PrimaryButtonConfig } from 'viewpointBuilder/openViewpointBuilder/getPrimaryButtonConfig';
import { WithMetamodelLoader } from '../components/WithMetamodelLoader';
import { TraversalBuilderViewState } from '../types';
import { ResizablePageBody } from '@ardoq/page-layout';
import { FloatingClickOtherComponentsHint } from './FloatingClickOtherComponentsHint';
import type { I18nLocale } from '@ardoq/i18n';

type BuildSubgraphContentProps = {
  state: TraversalBuilderViewState;
  commands: EditTraversalCommands;
  primaryButtonConfig: PrimaryButtonConfig;
  onCancel: VoidFunction;
  label?: string;
  i18nLocale: I18nLocale;
};

export const BuildSubgraphContent = ({
  state: {
    graphNodes,
    graphEdges,
    graphGroups,
    hasTripleOptions,
    filteredTripleOptions,
    selectedGraphNodeId,
    triplesSortAndFiltersState,
    isMetaModelLoaded,
    shouldShowDeleteCurrentNodeButton,
    shouldShowStartSetExpansionToggle,
    hierarchyComponentTypes,
    startSetExpansion,
    shouldIncludeClickOtherComponentTypeNodesNotification,
    wildcardState,
  },
  commands,
  primaryButtonConfig,
  onCancel,
  label = 'Dataset metamodel',
  i18nLocale,
}: BuildSubgraphContentProps) => {
  const graphInterface = useRef(createGraphInterface());
  const toggleGraphNodeSelection = useMemo(
    () => getToggleGraphNodeSelection(commands),
    [commands]
  );
  return (
    <WithMetamodelLoader
      isMetaModelLoaded={isMetaModelLoaded}
      closeViewpointBuilder={onCancel}
      i18nLocale={i18nLocale}
    >
      <ResizablePageBody
        skipPadding
        footerContent={
          <ViewpointBuilderFooter
            closeViewpointBuilder={onCancel}
            primaryButtonConfig={primaryButtonConfig}
            i18nLocale={i18nLocale}
          />
        }
        rightContentInitialWidth={600}
        rightContent={
          <EditTraversalOptionsPanel
            selectedGraphNodeId={selectedGraphNodeId}
            tripleOptions={filteredTripleOptions}
            hasTripleOptions={hasTripleOptions}
            commands={commands}
            triplesSortAndFiltersState={triplesSortAndFiltersState}
            shouldShowDeleteCurrentNodeButton={
              shouldShowDeleteCurrentNodeButton
            }
            shouldShowStartSetExpansionToggle={
              shouldShowStartSetExpansionToggle
            }
            hierarchyComponentTypes={hierarchyComponentTypes}
            startSetExpansion={startSetExpansion}
            wildcardState={wildcardState}
            i18nLocale={i18nLocale}
          />
        }
      >
        <SimpleGraphContainerWithGreyBackground
          onClick={toggleGraphNodeSelection}
        >
          {shouldIncludeClickOtherComponentTypeNodesNotification && (
            <FloatingClickOtherComponentsHint
              onClickNeverShowAgainHints={
                commands.skipClickOtherComponentTypeNodesHints
              }
              i18nLocale={i18nLocale}
            />
          )}
          <SimpleGraphView
            viewModel={{ graphNodes, graphEdges, graphGroups }}
            graphInterface={graphInterface.current}
            label={label}
          />
        </SimpleGraphContainerWithGreyBackground>
      </ResizablePageBody>
    </WithMetamodelLoader>
  );
};
